import React from 'react';
import Link from 'next/link';
import { TextLinkProps } from './types';
import { addTrailingSlashToInternalUrl } from '@/lib/utils/trailingSlash';

const TEXT_LINK_STYLES = {
  base: `
    relative inline-block
    font-instrument font-semibold leading-[140%]
    transition-colors duration-200
    focus:outline-none focus:ring-2 focus:ring-offset-2

    after:absolute
    after:left-0
    after:-bottom-2
    after:h-0.5
    after:w-full
    after:transition-all
    after:duration-200
  `,
  size: {
    sm: 'text-xs',
    base: 'text-sm',
    lg: 'text-base',
  },
  variant: {
    primary: `
      text-primary-500
      after:bg-secondary-500
      hover:text-primary-800
      hover:after:-bottom-1
      focus:ring-secondary-300
    `,
    secondary: `
      text-secondary-500
      after:bg-primary-500
      hover:text-secondary-700
      hover:after:-bottom-1
      focus:ring-primary-300
    `,
  },
} as const;

export const TextLink: React.FC<TextLinkProps> = ({
  href,
  text,
  size = 'base',
  variant = 'primary',
  uppercase = false,
  className = '',
  target,
  rel,
  children,
  ariaLabel,
  srOnlyText,
}) => {
  const sizeClass = TEXT_LINK_STYLES.size[size];
  const variantClass = TEXT_LINK_STYLES.variant[variant];
  const uppercaseClass = uppercase ? 'uppercase tracking-wider' : '';
  const normalizedHref = addTrailingSlashToInternalUrl(href);
  const linkContent = children || text;

  return (
    <Link
      href={normalizedHref}
      target={target}
      rel={rel}
      className={`${TEXT_LINK_STYLES.base} ${sizeClass} ${variantClass} ${uppercaseClass} ${className}`.trim()}
      aria-label={ariaLabel}
    >
      {linkContent}
      {srOnlyText ? <span className='sr-only'>{srOnlyText}</span> : null}
    </Link>
  );
};

export default TextLink;
